import React, { useEffect, useState, useRef } from "react";
import { FormApi } from "final-form";
import { useParams } from "react-router-dom";
import { Form, Field } from "react-final-form";
import axios from "axios";
import { loadStripe, StripeElementsOptions } from "@stripe/stripe-js";
import {
    Elements,
    useStripe,
    useElements,
    PaymentElement,
} from "@stripe/react-stripe-js";
import UserDetailsModal from "../components/UserDetailsModal";
import FloatingField from "../components/FloatingField";
import ImpactModal from "../components/ImpactModal";

const StripeForm: React.FC<{ onSuccess: () => void }> = ({ onSuccess }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!stripe || !elements) return;

        setLoading(true);
        const res = await axios.get(process.env.REACT_APP_API_URL + "user", {
            headers: {
                token: localStorage.getItem("token"),
            },
        });
        const { error, paymentIntent } = await stripe.confirmPayment({
            elements,
            confirmParams: {
                return_url: window.location.href,
                payment_method_data: {
                    billing_details: {
                        name: res.data.name,
                        email: res.data.email,
                        address: {
                            line1: "N/A",
                            city: "Mumbai",
                            postal_code: "400001",
                            country: "IN",
                        },
                    },
                },
            },
            redirect: "if_required",
        });

        if (error) {
            alert(error.message);
        } else if (paymentIntent?.status === "succeeded") {
            await axios.post(
                process.env.REACT_APP_API_URL + "offset/update-offset-payment",
                {
                    stripeId: paymentIntent.id,
                },
                {
                    headers: {
                        token: localStorage.getItem("token"),
                    },
                }
            );
            onSuccess();
        }

        setLoading(false);
    };

    return (
        <form onSubmit={handleSubmit}>
            <PaymentElement />
            <button
                type="submit"
                style={{
                    marginTop: "20px",
                    width: "100%",
                    padding: "10px",
                    borderRadius: "8px",
                    border: "none",
                    background: "#8DD3BB",
                    fontWeight: "bold",
                    fontSize: "18px",
                }}
                disabled={loading}
            >
                {loading ? "Processing..." : "Pay Now"}
            </button>
        </form>
    );
};

const Home: React.FC = () => {
    const [formValues, setFormValues] = useState<any>({});
    const [offsetData, setOffsetData] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [successModalIsOpen, setSuccessModalIsOpen] = useState(false);
    const [impactResponse, setImpactResponse] = useState({} as any);
    const { link } = useParams();

    const scrollRef = useRef<HTMLDivElement>(null);
    const handleScroll = () => {
        scrollRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    const stripePromise = loadStripe(
        process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ""
    );
    const [clientSecret, setClientSecret] = useState<string | null>(null);
    const [showStripeForm, setShowStripeForm] = useState(false);

    const formRef = useRef<FormApi | null>(null);

    const accommodationOptions = [
        { label: "Hostel", value: "hostel" },
        { label: "3 Star Hotel", value: "3-star" },
        { label: "5 Star Hotel", value: "5-star" },
        { label: "Service Apartment", value: "service-apartment" },
        { label: "Resort", value: "resort" },
        { label: "Luxury Resort", value: "luxury-resort" },
        { label: "Homestay", value: "homestay" },
    ];

    const [impactData, setImpactData] = useState<any>(null);

    useEffect(() => {
        fetchImpactData();
    }, []);

    useEffect(() => {
        if (link && formRef.current) {
            fetchAccomodation(formRef.current);
        }
    }, [link]);

    const fetchImpactData = async () => {
        try {
            const response = await axios.get(
                "https://rethink.getre.org/api/initiative"
            );
            setImpactData(response.data);
        } catch (error) {
            console.error("Error fetching impact data:", error);
        }
    };

    const fetchAccomodation = async (form: any) => {
        try {
            const res = await axios.get(
                process.env.REACT_APP_API_URL + "/accomodation?link=" + link
            );
            form.change("type", res.data.type);
        } catch (error) {
            console.error("Error fetching accommodation:", error);
        }
    };

    const handleChange = (fieldName: string, value: any) => {
        const formValues: any = formRef.current?.getState().values;

        if (
            formValues.type &&
            formValues.days > 0 &&
            formValues.rooms > 0 &&
            formValues.people > 0
        ) {
            fetchOffset(formValues);
        }
    };

    const fetchOffset = async (values: any) => {
        try {
            setIsLoading(true);
            const res = await axios.post(
                process.env.REACT_APP_API_URL + "offset/calculate-offset",
                {
                    type: values.type,
                    people: parseInt(values.people),
                    rooms: parseInt(values.rooms),
                    days: parseInt(values.days),
                }
            );
            setOffsetData(res.data);
        } catch (error) {
            console.error("Error calculating offset:", error);
            setOffsetData(null);
        } finally {
            setIsLoading(false);
        }
    };

    const onSubmit = async (values: any) => {
        console.log(values);
        if (
            values.type &&
            values.days > 0 &&
            values.rooms > 0 &&
            values.people > 0
        ) {
            const token = localStorage.getItem("token");
            if (token) {
                createOffset();
            } else {
                setShowUserModal(true);
            }
        }
    };

    const createOffset = async () => {
        const formValues: any = formRef.current?.getState().values;
        const token = localStorage.getItem("token");
        const res = await axios.post(
            process.env.REACT_APP_API_URL + "offset",
            {
                amount: offsetData.offsetCost,
                accomodationType: formValues.type,
                people: formValues.people,
                rooms: formValues.rooms,
                days: formValues.days,
                impact: offsetData.totalCO2eEmissions,
                link: link || "rethink",
            },
            {
                headers: {
                    token,
                },
            }
        );

        const { clientSecret } = res.data;
        setImpactResponse(res.data);
        setClientSecret(clientSecret);
        setShowStripeForm(true);
    };

    const required = (value: any) => (value ? undefined : "Required");
    const [showUserModal, setShowUserModal] = useState(false);

    const handleUserDetailsSubmit = async (userDetails: any) => {
        console.log("User details:", userDetails);
        setShowUserModal(false);
    };

    const showStripeSection = showStripeForm && !!clientSecret;

    return (
        <div>
            <UserDetailsModal
                modalIsOpen={showUserModal}
                closeModal={() => setShowUserModal(false)}
                onUserCreated={() => {
                    createOffset();
                }}
            />
            <ImpactModal
                successModalIsOpen={successModalIsOpen}
                closeModal={() => setSuccessModalIsOpen(false)}
                message={impactResponse?.message}
                username={impactResponse?.username}
                impact={impactResponse?.impact}
            />
            {showStripeForm && clientSecret && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        zIndex: 9999,
                        width: "100vw",
                        height: "100vh",
                        backgroundColor: "rgba(0,0,0,0.7)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <div
                        style={{
                            background: "#fff",
                            width: "90%",
                            maxWidth: "500px",
                            borderRadius: "16px",
                            padding: "30px",
                            boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
                            position: "relative",
                        }}
                    >
                        <button
                            onClick={() => setShowStripeForm(false)}
                            style={{
                                position: "absolute",
                                top: "10px",
                                right: "16px",
                                background: "transparent",
                                border: "none",
                                fontSize: "24px",
                                cursor: "pointer",
                            }}
                        >
                            ×
                        </button>
                        <Elements
                            stripe={stripePromise}
                            options={{ clientSecret } as StripeElementsOptions}
                        >
                            <StripeForm
                                onSuccess={() => {
                                    setSuccessModalIsOpen(true);
                                    setShowStripeForm(false);
                                }}
                            />
                        </Elements>
                    </div>
                </div>
            )}
            <div className="banner-bg">
                <img
                    className="img img-fluid"
                    src="assets/images/bg-banner.png"
                    alt=""
                />
                <div
                    style={{
                        position: "absolute",
                        top: "5%",
                        backgroundColor: "white",
                        width: "70%",
                        padding: "20px",
                        borderTopRightRadius: "50px",
                        borderBottomRightRadius: "50px",
                    }}
                >
                    <h1
                        style={{
                            fontSize: "32px",
                            fontFamily: "Lato",
                            fontWeight: "bold",
                        }}
                    >
                        Discover Your Impact
                    </h1>
                    <p
                        style={{
                            fontFamily: "Lato",
                            fontSize: "16px",
                            textAlign: "justify",
                        }}
                    >
                        Your travel choices matter! Make your stay carbon
                        neutral while contributing to impactful Green Projects
                        worldwide.
                    </p>
                </div>

                {/* <div className="banner-content">
                    <h1>Welcome to Our Site</h1>
                    <p>Discover amazing content and more!</p>
                </div> */}
            </div>
            <Form
                onSubmit={onSubmit}
                render={({ handleSubmit, form }) => {
                    formRef.current = form;
                    return (
                        <form onSubmit={handleSubmit}>
                            <div
                                ref={scrollRef}
                                style={{
                                    position: "relative",
                                    backgroundColor: "white",
                                    border: "1px solid #ddd",
                                    borderRadius: "30px",
                                    boxShadow:
                                        "0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.06)",
                                    padding: "20px",
                                    marginLeft: "30px",
                                    marginRight: "30px",
                                    marginTop: "-220px",
                                    marginBottom: "40px",
                                }}
                            >
                                <h2
                                    style={{
                                        fontFamily: "Alata",
                                        fontSize: "20px",
                                        lineHeight: "1.5",
                                        textAlign: "center",
                                        paddingLeft: "30px",
                                        paddingRight: "30px",
                                    }}
                                >
                                    Let’s calculate your stay’s carbon
                                    footprint!
                                </h2>

                                <div
                                    style={{
                                        backgroundColor: "#FAFAFA",
                                        borderRadius: "30px",
                                        boxShadow:
                                            "0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.06)",
                                    }}
                                >
                                    <FloatingField
                                        name="type"
                                        label="Stay Type"
                                        type="select"
                                        disabled={!!link}
                                        options={accommodationOptions}
                                        handleChange={handleChange}
                                        validate={required}
                                    />
                                    <FloatingField
                                        name="days"
                                        label="How many days was your stay?"
                                        type="number"
                                        handleChange={handleChange}
                                        validate={required}
                                    />
                                    <FloatingField
                                        name="rooms"
                                        label="How many rooms did you book?"
                                        type="number"
                                        handleChange={handleChange}
                                        validate={required}
                                    />
                                    <FloatingField
                                        name="people"
                                        label="How many people stayed?"
                                        type="number"
                                        handleChange={handleChange}
                                        validate={required}
                                    />
                                </div>

                                {isLoading ? (
                                    <div
                                        style={{
                                            textAlign: "center",
                                            marginTop: "20px",
                                        }}
                                    >
                                        Calculating...
                                    </div>
                                ) : (
                                    offsetData && (
                                        <>
                                            <h1
                                                style={{
                                                    textAlign: "center",
                                                    fontFamily: "Alata",
                                                    fontSize: "20px",
                                                    marginTop: "20px",
                                                }}
                                            >
                                                Carbon footprint generated:
                                            </h1>
                                            <div
                                                style={{
                                                    marginTop: "10px",
                                                    background: "#FAFAFA",
                                                    borderRadius: "30px",
                                                    boxShadow:
                                                        "0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.06)",
                                                }}
                                            >
                                                <h1
                                                    style={{
                                                        textAlign: "center",
                                                        fontFamily: "Alata",
                                                        fontSize: "24px",
                                                        margin: 0,
                                                        marginBottom: "5px",
                                                    }}
                                                >
                                                    {offsetData.totalCO2eEmissions.toFixed(
                                                        2
                                                    )}{" "}
                                                    kg of CO<sub>2</sub>e*
                                                </h1>
                                                <div
                                                    style={{
                                                        fontSize: "17px",
                                                        textAlign: "center",
                                                        fontFamily: "Alata",
                                                        padding: "5px",
                                                    }}
                                                >
                                                    Offset your footprint for
                                                    just{" "}
                                                    <span
                                                        style={{
                                                            fontWeight: "bold",
                                                        }}
                                                    >
                                                        ₹
                                                        {offsetData.offsetCost.toFixed(
                                                            0
                                                        )}
                                                    </span>{" "}
                                                    and make your stay greener!
                                                </div>
                                            </div>
                                        </>
                                    )
                                )}
                                <div
                                    style={{
                                        fontFamily: "Alata",
                                        textAlign: "center",
                                        marginTop: "20px",
                                    }}
                                >
                                    Offset cost per Kg of CO2e is ₹5
                                </div>
                                <div
                                    className="terms-text"
                                    style={{ marginTop: "20px" }}
                                >
                                    <Field
                                        name="agree"
                                        type="checkbox"
                                        validate={required}
                                    >
                                        {({ input, meta }) => (
                                            <div
                                                style={{
                                                    display: "flex",
                                                    flexWrap: "wrap",
                                                    fontSize:
                                                        "clamp(10px, 3.5vw, 13px)", // responsive font size
                                                    lineHeight: "1.4",
                                                }}
                                            >
                                                <input
                                                    {...input}
                                                    type="checkbox"
                                                    id="agree"
                                                    style={{
                                                        marginRight: "6px",
                                                        flexShrink: 0,
                                                    }}
                                                />
                                                <label
                                                    htmlFor="agree"
                                                    style={{
                                                        margin: 0,
                                                        flex: 1,
                                                        minWidth: 0,
                                                        wordBreak: "break-word",
                                                    }}
                                                >
                                                    I agree to{" "}
                                                    <a
                                                        href="/terms"
                                                        target="_blank"
                                                    >
                                                        terms of service
                                                    </a>
                                                    &nbsp;and&nbsp;
                                                    <a href="/privacy-policy">
                                                        privacy policy
                                                    </a>
                                                </label>
                                            </div>
                                        )}
                                    </Field>

                                    <Field
                                        name="agree"
                                        subscription={{
                                            touched: true,
                                            error: true,
                                        }}
                                    >
                                        {({ meta }) =>
                                            meta.touched && meta.error ? (
                                                <div
                                                    style={{
                                                        color: "red",
                                                        fontSize: "13px",
                                                        marginTop: "5px",
                                                        textAlign: "left",
                                                    }}
                                                >
                                                    {meta.error}
                                                </div>
                                            ) : null
                                        }
                                    </Field>
                                </div>

                                <button
                                    type="submit"
                                    className="offset-btn"
                                    style={{
                                        marginTop: "20px",
                                        fontFamily: "Alata",
                                        width: "100%",
                                    }}
                                >
                                    Offset Now
                                </button>
                            </div>
                        </form>
                    );
                }}
            />
            <section className="color-section">
                <h1 className="section-heading">Why Offset?</h1>
                <p
                    style={{
                        fontFamily: "Lato",
                        fontSize: "18px",
                        margin: "20px",
                        textAlign: "left",
                    }}
                >
                    <div style={{ marginBottom: "10px" }}>
                        <b>Every Journey Leaves a Mark</b>
                    </div>
                    <div
                        style={{
                            marginBottom: "10px",
                            textAlign: "justify",
                        }}
                    >
                        Traveling is exciting, but it comes with a carbon cost.
                        The good news? You can neutralize your stay’s footprint
                        and make a difference with just one click. Offset your
                        emissions responsibly and help protect the planet.
                    </div>
                    <ul
                        style={{
                            paddingLeft: "16px",
                        }}
                    >
                        <li style={{ textAlign: "justify" }}>
                            <b>Carbon footprint released per stay/day:</b>{" "}
                            Approximately 6-70 kg CO<sub>2</sub>e*.
                        </li>
                        <li style={{ textAlign: "justify" }}>
                            <b>Global impact:</b> Travelers contribute to 8%* of
                            global carbon emissions.
                        </li>
                    </ul>
                    <div style={{ textAlign: "center" }}>
                        <b>Let’s change that together.</b>
                    </div>
                </p>
                <div style={{ textAlign: "center" }}>
                    <button className="offset-btn" onClick={handleScroll}>
                        Offset Your Stay
                    </button>
                </div>
            </section>
            <section className="white-section">
                <h1 className="section-heading">Impact So Far</h1>
                <div
                    style={{
                        display: "flex",
                        flex: 1,
                        justifyContent: "space-between",
                    }}
                >
                    <div style={{ flex: 1 }}>
                        <div
                            className="impact-card"
                            style={{
                                backgroundImage: `linear-gradient(rgba(226, 241, 232, 0.3), rgba(226, 241, 232, 0.3)), 
                        linear-gradient(rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.9)), 
                        url('assets/images/location-bg.png')`,
                            }}
                        >
                            <div className="impact-card-title">
                                Impacters From
                            </div>
                            <div className="impact-card-text">
                                {impactData
                                    ? impactData.locations
                                    : "Loading..."}{" "}
                                Locations
                            </div>
                        </div>
                    </div>
                    <div style={{ flex: 1 }}>
                        <div
                            className="impact-card"
                            style={{
                                backgroundImage: `
                            linear-gradient(rgba(226, 241, 232, 0.3), rgba(226, 241, 232, 0.3)), 
                            linear-gradient(rgba(255, 255, 255, 0.85), rgba(255, 255, 255, 0.85)), 
                            url('assets/images/warriors-bg.png')`,
                            }}
                        >
                            <div className="impact-card-title">
                                Sustainable Travelers
                            </div>
                            <div className="impact-card-text">
                                {impactData
                                    ? impactData.totalUsers
                                    : "Loading..."}{" "}
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    style={{
                        display: "flex",
                        flex: 1,
                        justifyContent: "space-between",
                    }}
                >
                    <div style={{ flex: 1 }}>
                        <div
                            className="impact-card"
                            style={{
                                backgroundImage: `
                            linear-gradient(rgba(226, 241, 232, 0.3), rgba(226, 241, 232, 0.3)), 
                            linear-gradient(rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.9)), 
                            url('assets/images/impact-bg.png')`,
                            }}
                        >
                            <div className="impact-card-title">
                                Carbon Neutral Stays
                            </div>
                            <div className="impact-card-text">10</div>
                        </div>
                    </div>
                    <div style={{ flex: 1 }}>
                        <div
                            className="impact-card"
                            style={{
                                backgroundImage: `
                            linear-gradient(rgba(226, 241, 232, 0.3), rgba(226, 241, 232, 0.3)), 
                            linear-gradient(rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.9)), 
                            url('assets/images/impact-bg.png')`,
                            }}
                        >
                            <div className="impact-card-title">
                                Total CO2 offset
                            </div>
                            <div className="impact-card-text">
                                {impactData
                                    ? Math.floor(impactData.totalImpact / 1000)
                                    : "Loading..."}{" "}
                                Kgs of CO2e*
                            </div>
                            {/* <div className="impact-card-subtext">
                                Saved/year
                            </div> */}
                            <div
                                className="impact-card-subtext"
                                style={{
                                    fontSize: "12px",
                                    fontStyle: "italic",
                                    marginRight: "10px",
                                    marginLeft: "10px",
                                    textAlign: "center",
                                    position: "absolute",
                                    bottom: "5px",
                                }}
                            >
                                Equivalent to driving{" "}
                                <b>
                                    {impactData
                                        ? Math.floor(
                                              impactData.totalImpact / 1000
                                          ) * 4
                                        : "Loading..."}{" "}
                                </b>
                                kms by car
                            </div>
                        </div>
                    </div>
                </div>

                <div style={{ textAlign: "center", marginTop: "10px" }}>
                    <button className="offset-btn" onClick={handleScroll}>
                        Offset Your Footprint
                    </button>
                </div>
            </section>
            <section className="color-section">
                <div></div>
                <h1 className="section-heading">Transparency Matters</h1>
                <p className="section-p">
                    Your trust is everything. That’s why we ensure full
                    transparency about how your offset contribution is used and
                    the real impact it creates. No hidden details, no vague
                    promises - just clear actions.
                </p>
                {/* <h4
                    style={{
                        textAlign: "center",
                        marginTop: "15px",
                        marginBottom: "15px",
                        fontFamily: "Alata",
                    }}
                >
                    How We Create Transparency
                </h4> */}
                <section className="faq" style={{ marginBottom: "20px" }}>
                    <div className="col-md-12 col-12">
                        <div className="accordion" id="accordionExample1">
                            <div className="accordion-item">
                                <h2 className="accordion-header">
                                    <button
                                        className="accordion-button faq-question"
                                        type="button"
                                        data-bs-toggle="collapse"
                                        data-bs-target="#collapseOne1"
                                        aria-expanded="true"
                                        aria-controls="collapseOne1"
                                    >
                                        <h4
                                            style={{
                                                textAlign: "center",
                                                color: "#000000",
                                                fontFamily: "Alata",
                                            }}
                                        >
                                            Where Your Contribution Goes
                                        </h4>
                                    </button>
                                </h2>
                                <div
                                    id="collapseOne1"
                                    className="accordion-collapse collapse faq-answer show"
                                    data-bs-parent="#accordionExample1"
                                >
                                    <div className="accordion-body">
                                        <div
                                            style={{
                                                fontFamily: "Lato",
                                                fontSize: "18px",

                                                textAlign: "center",
                                            }}
                                        >
                                            <div
                                                style={{ textAlign: "justify" }}
                                            >
                                                Every rupee you contribute is
                                                put to work responsibly. Here’s
                                                the breakdown:
                                            </div>

                                            <div
                                                style={{
                                                    display: "flex",
                                                    justifyContent: "center",
                                                }}
                                            >
                                                <div className="contribution-card">
                                                    <img
                                                        src="/assets/images/icons/tree.svg"
                                                        alt="Green Projects"
                                                        width={60}
                                                        height={60}
                                                    />
                                                    <div
                                                        style={{
                                                            marginTop: "10px",
                                                        }}
                                                    >
                                                        <strong>80-90%</strong>
                                                        <br />
                                                        Green Projects
                                                    </div>
                                                </div>

                                                <div className="contribution-card">
                                                    <img
                                                        src="/assets/images/icons/settings.svg"
                                                        alt="Operational Costs"
                                                        width={60}
                                                        height={60}
                                                    />
                                                    <div
                                                        style={{
                                                            marginTop: "10px",
                                                        }}
                                                    >
                                                        <strong>5-10%</strong>
                                                        <br />
                                                        Operations
                                                    </div>
                                                </div>
                                            </div>
                                            <div
                                                style={{
                                                    display: "flex",
                                                    justifyContent: "center",
                                                }}
                                            >
                                                <div
                                                    className="contribution-card"
                                                    style={{ flex: 0.5 }}
                                                >
                                                    <img
                                                        src="/assets/images/icons/megaphone.svg"
                                                        alt="Outreach and Education"
                                                        width={60}
                                                        height={60}
                                                    />
                                                    <div
                                                        style={{
                                                            marginTop: "10px",
                                                        }}
                                                    >
                                                        <strong>7-10%</strong>
                                                        <br />
                                                        Outreach
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="accordion-item">
                                <h2 className="accordion-header">
                                    <button
                                        className="accordion-button faq-question"
                                        type="button"
                                        data-bs-toggle="collapse"
                                        data-bs-target="#collapseOne2"
                                        aria-expanded="true"
                                        aria-controls="collapseOne2"
                                    >
                                        <h4
                                            style={{
                                                textAlign: "center",
                                                color: "#000000",
                                                fontFamily: "Alata",
                                            }}
                                        >
                                            Track Your Contribution
                                        </h4>
                                    </button>
                                </h2>
                                <div
                                    id="collapseOne2"
                                    className="accordion-collapse collapse faq-answer"
                                    data-bs-parent="#accordionExample2"
                                >
                                    <div className="accordion-body">
                                        <div
                                            style={{
                                                fontFamily: "Lato",
                                                fontSize: "18px",

                                                textAlign: "justify",
                                            }}
                                        >
                                            We believe you should see your
                                            impact. Here’s how we keep you in
                                            the loop:
                                            <br />
                                            <div>
                                                <h4
                                                    style={{
                                                        textAlign: "center",
                                                        fontFamily: "Alata",
                                                        margin: "15px",
                                                    }}
                                                >
                                                    Confirmation
                                                </h4>
                                                {/* <p
                                                    style={{
                                                        fontFamily: "Lato",
                                                        fontSize: "18px",
                                                        textAlign: "justify",
                                                    }}
                                                >
                                                    Get notified when your funds
                                                    are received.
                                                </p> */}
                                                <img
                                                    src="assets/images/confirmation.jpg"
                                                    style={{
                                                        boxShadow:
                                                            "0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.06)",
                                                        width: "100%",
                                                        height: "auto",
                                                        borderRadius: "20px",
                                                    }}
                                                />
                                            </div>
                                            <div>
                                                <h4
                                                    style={{
                                                        textAlign: "center",
                                                        fontFamily: "Alata",
                                                        margin: "15px",
                                                    }}
                                                >
                                                    Project
                                                </h4>
                                                <img
                                                    src="assets/images/project.jpg"
                                                    style={{
                                                        boxShadow:
                                                            "0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.06)",
                                                        width: "100%",
                                                        height: "auto",
                                                        borderRadius: "20px",
                                                    }}
                                                />
                                            </div>
                                            <div>
                                                <h4
                                                    style={{
                                                        textAlign: "center",
                                                        fontFamily: "Alata",
                                                        margin: "15px",
                                                    }}
                                                >
                                                    Impact
                                                </h4>
                                                <div
                                                    style={{
                                                        textAlign: "center",
                                                        marginTop: "10px",
                                                    }}
                                                >
                                                    <button className="offset-btn">
                                                        See our impact
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="accordion-item">
                                <h2 className="accordion-header">
                                    <button
                                        className="accordion-button faq-question"
                                        type="button"
                                        data-bs-toggle="collapse"
                                        data-bs-target="#collapseOne3"
                                        aria-expanded="true"
                                        aria-controls="collapseOne3"
                                    >
                                        <h4
                                            style={{
                                                textAlign: "center",
                                                color: "#000000",
                                                fontFamily: "Alata",
                                            }}
                                        >
                                            Why Transparency
                                        </h4>
                                    </button>
                                </h2>
                                <div
                                    id="collapseOne3"
                                    className="accordion-collapse collapse faq-answer"
                                    data-bs-parent="#accordionExample3"
                                >
                                    <div className="accordion-body">
                                        <div
                                            style={{
                                                fontFamily: "Lato",
                                                fontSize: "18px",
                                                textAlign: "justify",
                                            }}
                                        >
                                            Transparency isn’t just a feature
                                            for us - it’s a commitment. We want
                                            you to feel confident that your
                                            offsets are real, measurable, and
                                            meaningful.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <div style={{ textAlign: "center" }}>
                    <button className="offset-btn" onClick={handleScroll}>
                        Offset Your Footprint
                    </button>
                </div>
            </section>
            <section className="white-section">
                <h1 className="section-heading">Green Projects</h1>
                <p
                    style={{
                        fontFamily: "Lato",
                        fontSize: "18px",
                        margin: "20px",
                        textAlign: "justify",
                    }}
                >
                    We offset your footprint through Green Projects -
                    initiatives that neutralise your stay’s carbon footprint
                    effectively and responsibly while creating positive
                    environmental and social impacts.
                    <br />
                    <br />
                    Your stay footprint is offset through these impactful
                    projects (examples):
                </p>
                <div className="faq">
                    <div className="col-md-12 col-12">
                        <div className="accordion" id="accordionExample3">
                            <div className="accordion-item">
                                <h2 className="accordion-header">
                                    <button
                                        className="accordion-button faq-question-green-project"
                                        type="button"
                                        data-bs-toggle="collapse"
                                        data-bs-target="#collapseOne10"
                                        aria-expanded="true"
                                        aria-controls="collapseOne10"
                                        style={{
                                            padding: "10px 0px 0px 0px",
                                        }}
                                    >
                                        <h4
                                            style={{
                                                textAlign: "center",
                                                color: "#000000",
                                                fontFamily: "Alata",
                                                width: "100%",
                                            }}
                                        >
                                            Earth
                                        </h4>
                                    </button>
                                </h2>
                                <div
                                    id="collapseOne10"
                                    className="accordion-collapse show faq-answer-green-project"
                                    data-bs-parent="#accordionExample3"
                                >
                                    <div className="accordion-body">
                                        <div
                                            style={{
                                                fontFamily: "Lato",
                                                fontSize: "18px",
                                                textAlign: "center",
                                                flex: 1,
                                                display: "flex",
                                            }}
                                        >
                                            <div
                                                className="contribution-card"
                                                style={{
                                                    display: "flex",
                                                    flex: 0.5,
                                                    flexDirection: "column",
                                                }}
                                            >
                                                <img
                                                    src="/assets/images/tree-plantation.jpg"
                                                    alt="Tree Plantation"
                                                    style={{
                                                        width: "100%",
                                                        borderRadius: "30px",
                                                        height: "100px",
                                                    }}
                                                />
                                                <h4
                                                    style={{
                                                        fontFamily: "Alata",
                                                        fontSize: "16px",
                                                        marginTop: "10px",
                                                        textAlign: "center",
                                                    }}
                                                >
                                                    Tree
                                                    <br /> Plantation
                                                </h4>
                                                <div
                                                    style={{
                                                        fontSize: "16px",
                                                        textAlign: "center",
                                                    }}
                                                >
                                                    CO₂e is absorbed by planting
                                                    trees, contributing to
                                                    reforestation and
                                                    biodiversity restoration.
                                                </div>
                                            </div>

                                            <div
                                                className="contribution-card"
                                                style={{
                                                    display: "flex",
                                                    flex: 0.5,
                                                    flexDirection: "column",
                                                }}
                                            >
                                                <img
                                                    src="/assets/images/mangrove-restoration.jpg"
                                                    alt="Tree Plantation"
                                                    style={{
                                                        width: "100%",
                                                        borderRadius: "30px",
                                                        height: "100px",
                                                    }}
                                                />
                                                <h4
                                                    style={{
                                                        fontFamily: "Alata",
                                                        fontSize: "16px",
                                                        marginTop: "10px",
                                                        textAlign: "center",
                                                    }}
                                                >
                                                    Mangrove Restoration
                                                </h4>
                                                <div
                                                    style={{
                                                        fontSize: "16px",
                                                        textAlign: "center",
                                                    }}
                                                >
                                                    Mangroves absorb up to 4x
                                                    more CO₂e than regular
                                                    forests while protecting
                                                    coastal ecosystems.
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="accordion-item">
                                <h2 className="accordion-header">
                                    <button
                                        className="accordion-button faq-question-green-project"
                                        type="button"
                                        data-bs-toggle="collapse"
                                        data-bs-target="#collapseOne20"
                                        aria-expanded="true"
                                        aria-controls="collapseOne20"
                                        style={{
                                            padding: "10px 0px 0px 0px",
                                        }}
                                    >
                                        <h4
                                            style={{
                                                textAlign: "center",
                                                color: "#000000",
                                                fontFamily: "Alata",
                                                width: "100%",
                                            }}
                                        >
                                            Air
                                        </h4>
                                    </button>
                                </h2>
                                <div
                                    id="collapseOne20"
                                    className="accordion-collapse collapse faq-answer-green-project"
                                    data-bs-parent="#accordionExample3"
                                >
                                    <div className="accordion-body">
                                        <div
                                            style={{
                                                fontFamily: "Lato",
                                                fontSize: "18px",
                                                textAlign: "center",
                                                flex: 1,
                                                display: "flex",
                                            }}
                                        >
                                            <div
                                                className="contribution-card"
                                                style={{
                                                    display: "flex",
                                                    flex: 0.5,
                                                    flexDirection: "column",
                                                }}
                                            >
                                                <img
                                                    src="/assets/images/clean-cookstove.jpg"
                                                    alt="Clean Cookstoves"
                                                    style={{
                                                        width: "100%",
                                                        borderRadius: "30px",
                                                        height: "100px",
                                                    }}
                                                />
                                                <h4
                                                    style={{
                                                        fontFamily: "Alata",
                                                        fontSize: "16px",
                                                        marginTop: "10px",
                                                        textAlign: "center",
                                                    }}
                                                >
                                                    Clean Cookstoves
                                                </h4>
                                                <div
                                                    style={{
                                                        fontSize: "16px",
                                                        textAlign: "center",
                                                    }}
                                                >
                                                    Reducing emissions by
                                                    supporting the distribution
                                                    of energy-efficient
                                                    cookstoves in rural
                                                    communities.
                                                </div>
                                            </div>

                                            <div
                                                className="contribution-card"
                                                style={{
                                                    display: "flex",
                                                    flex: 0.5,
                                                    flexDirection: "column",
                                                }}
                                            >
                                                <img
                                                    src="/assets/images/biogas.jpg"
                                                    alt="Biogas Projects"
                                                    style={{
                                                        width: "100%",
                                                        borderRadius: "30px",
                                                        height: "100px",
                                                    }}
                                                />
                                                <h4
                                                    style={{
                                                        fontFamily: "Alata",
                                                        fontSize: "16px",
                                                        marginTop: "10px",
                                                        textAlign: "center",
                                                    }}
                                                >
                                                    Biogas Projects
                                                </h4>
                                                <div
                                                    style={{
                                                        fontSize: "16px",
                                                        textAlign: "center",
                                                    }}
                                                >
                                                    Converting organic waste
                                                    into clean energy, reducing
                                                    methane emissions and
                                                    providing sustainable fuel
                                                    for households.
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="accordion-item">
                                <h2 className="accordion-header">
                                    <button
                                        className="accordion-button faq-question-green-project"
                                        type="button"
                                        data-bs-toggle="collapse"
                                        data-bs-target="#collapseOne30"
                                        aria-expanded="true"
                                        aria-controls="collapseOne30"
                                        style={{
                                            padding: "10px 0px 0px 0px",
                                        }}
                                    >
                                        <h4
                                            style={{
                                                textAlign: "center",
                                                color: "#000000",
                                                fontFamily: "Alata",
                                                width: "100%",
                                            }}
                                        >
                                            Fire
                                        </h4>
                                    </button>
                                </h2>
                                <div
                                    id="collapseOne30"
                                    className="accordion-collapse collapse faq-answer-green-project"
                                    data-bs-parent="#accordionExample3"
                                >
                                    <div className="accordion-body">
                                        <div
                                            style={{
                                                fontFamily: "Lato",
                                                fontSize: "18px",
                                                textAlign: "center",
                                                flex: 1,
                                                display: "flex",
                                            }}
                                        >
                                            <div
                                                className="contribution-card"
                                                style={{
                                                    display: "flex",
                                                    flex: 0.5,
                                                    flexDirection: "column",
                                                }}
                                            >
                                                <img
                                                    src="/assets/images/solar.webp"
                                                    alt="Solar Energy"
                                                    style={{
                                                        width: "100%",
                                                        borderRadius: "30px",
                                                        height: "100px",
                                                    }}
                                                />
                                                <h4
                                                    style={{
                                                        fontFamily: "Alata",
                                                        fontSize: "16px",
                                                        marginTop: "10px",
                                                        textAlign: "center",
                                                    }}
                                                >
                                                    Solar Energy Projects
                                                </h4>
                                                <div
                                                    style={{
                                                        fontSize: "16px",
                                                        textAlign: "center",
                                                    }}
                                                >
                                                    Generating clean and
                                                    renewable energy for
                                                    underserved areas, replacing
                                                    fossil fuels and cutting
                                                    CO₂e.
                                                </div>
                                            </div>

                                            <div
                                                className="contribution-card"
                                                style={{
                                                    display: "flex",
                                                    flex: 0.5,
                                                    flexDirection: "column",
                                                }}
                                            >
                                                <img
                                                    src="/assets/images/energy-plant.jpg"
                                                    alt="Biogas Projects"
                                                    style={{
                                                        width: "100%",
                                                        borderRadius: "30px",
                                                        height: "100px",
                                                    }}
                                                />
                                                <h4
                                                    style={{
                                                        fontFamily: "Alata",
                                                        fontSize: "16px",
                                                        marginTop: "10px",
                                                        textAlign: "center",
                                                    }}
                                                >
                                                    Waste-to-Energy Initiatives
                                                </h4>
                                                <div
                                                    style={{
                                                        fontSize: "16px",
                                                        textAlign: "center",
                                                    }}
                                                >
                                                    Transforming non-recyclable
                                                    waste into energy, reducing
                                                    landfill dependency and
                                                    emissions.
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <section className="color-section">
                <h4 className="section-heading">FAQ'S</h4>
                <section className="faq">
                    <div className="col-md-12 col-12">
                        <div className="accordion" id="accordionExample">
                            <div className="accordion-item">
                                <h2 className="accordion-header">
                                    <button
                                        className="accordion-button faq-question"
                                        type="button"
                                        data-bs-toggle="collapse"
                                        data-bs-target="#collapseOne"
                                        aria-expanded="true"
                                        aria-controls="collapseOne"
                                    >
                                        What is carbon offsetting?
                                    </button>
                                </h2>
                                <div
                                    id="collapseOne"
                                    className="accordion-collapse collapse show faq-answer"
                                    data-bs-parent="#accordionExample"
                                >
                                    <div className="accordion-body">
                                        <p>
                                            Carbon offsetting is the process of
                                            compensating for carbon dioxide
                                            emissions by funding projects that
                                            reduce or absorb greenhouse gases
                                            elsewhere. For example, planting
                                            trees or generating renewable
                                            energy.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="accordion-item">
                                <h2 className="accordion-header">
                                    <button
                                        className="accordion-button collapsed faq-question"
                                        type="button"
                                        data-bs-toggle="collapse"
                                        data-bs-target="#collapseTwo"
                                        aria-expanded="true"
                                        aria-controls="collapseTwo"
                                    >
                                        Why should you care?
                                    </button>
                                </h2>
                                <div
                                    id="collapseTwo"
                                    className="accordion-collapse collapse faq-answer"
                                    data-bs-parent="#accordionExample"
                                >
                                    <div className="accordion-body">
                                        <p>
                                            Climate change affects everyone. By
                                            offsetting, you become part of the
                                            solution.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="accordion-item">
                                <h2 className="accordion-header">
                                    <button
                                        className="accordion-button collapsed faq-question"
                                        type="button"
                                        data-bs-toggle="collapse"
                                        data-bs-target="#collapseThree"
                                        aria-expanded="true"
                                        aria-controls="collapseThree"
                                    >
                                        How do carbon offset projects, like
                                        reforestation or renewable energy, help
                                        mitigate climate change?
                                    </button>
                                </h2>
                                <div
                                    id="collapseThree"
                                    className="accordion-collapse collapse faq-answer"
                                    data-bs-parent="#accordionExample"
                                >
                                    <div className="accordion-body">
                                        <p>
                                            Reforestation projects capture CO₂e
                                            through trees absorbing carbon as
                                            they grow. Renewable energy
                                            projects, like wind or solar,
                                            replace fossil fuels, which reduces
                                            emissions associated with energy
                                            production. Both types of projects
                                            help prevent further CO₂e emissions
                                            and actively remove carbon from the
                                            atmosphere.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="accordion-item">
                                <h2 className="accordion-header">
                                    <button
                                        className="accordion-button collapsed faq-question"
                                        type="button"
                                        data-bs-toggle="collapse"
                                        data-bs-target="#collapseFour"
                                        aria-expanded="true"
                                        aria-controls="collapseFour"
                                    >
                                        How does the ReThink Travel’s offsetting
                                        process work?
                                    </button>
                                </h2>
                                <div
                                    id="collapseFour"
                                    className="accordion-collapse collapse faq-answer"
                                    data-bs-parent="#accordionExample"
                                >
                                    <div className="accordion-body">
                                        <p>
                                            Simply calculate your emissions,
                                            choose to offset, and we’ll ensure
                                            your funds are used for Green
                                            Projects.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="accordion-item">
                                <h2 className="accordion-header">
                                    <button
                                        className="accordion-button collapsed faq-question"
                                        type="button"
                                        data-bs-toggle="collapse"
                                        data-bs-target="#collapseFive"
                                        aria-expanded="true"
                                        aria-controls="collapseFive"
                                    >
                                        Why do I need to provide my phone number
                                        and email address?
                                    </button>
                                </h2>
                                <div
                                    id="collapseFive"
                                    className="accordion-collapse collapse faq-answer"
                                    data-bs-parent="#accordionExample"
                                >
                                    <div className="accordion-body">
                                        <p>
                                            Your offset data, including
                                            confirmation of funds received and
                                            details of the offset projects, will
                                            be shared with you via WhatsApp (or
                                            text). Providing accurate contact
                                            details ensures you receive updates
                                            promptly.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="accordion-item">
                                <h2 className="accordion-header">
                                    <button
                                        className="accordion-button collapsed faq-question"
                                        type="button"
                                        data-bs-toggle="collapse"
                                        data-bs-target="#collapseSix"
                                        aria-expanded="true"
                                        aria-controls="collapseSix"
                                    >
                                        How can I trust that my contribution is
                                        making a difference?
                                    </button>
                                </h2>
                                <div
                                    id="collapseSix"
                                    className="accordion-collapse collapse faq-answer"
                                    data-bs-parent="#accordionExample"
                                >
                                    <div className="accordion-body">
                                        <p>
                                            We maintain transparency by
                                            notifying you when your funds are
                                            received and providing updates when
                                            they are used to offset emissions.
                                            You’ll also learn about the specific
                                            Green Projects your contribution
                                            supports.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="accordion-item">
                                <h2 className="accordion-header">
                                    <button
                                        className="accordion-button collapsed faq-question"
                                        type="button"
                                        data-bs-toggle="collapse"
                                        data-bs-target="#collapseSeven"
                                        aria-expanded="true"
                                        aria-controls="collapseSeven"
                                    >
                                        What happens if I choose not to offset
                                        my footprint?
                                    </button>
                                </h2>
                                <div
                                    id="collapseSeven"
                                    className="accordion-collapse collapse faq-answer"
                                    data-bs-parent="#accordionExample"
                                >
                                    <div className="accordion-body">
                                        <p>
                                            Offsetting is voluntary, but it’s an
                                            impactful way to balance your travel
                                            emissions. If you choose not to
                                            offset, consider adopting other
                                            sustainable travel practices,
                                            e.g.taking a train instead of a
                                            flight.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="accordion-item">
                                <h2 className="accordion-header">
                                    <button
                                        className="accordion-button collapsed faq-question"
                                        type="button"
                                        data-bs-toggle="collapse"
                                        data-bs-target="#collapseEight"
                                        aria-expanded="true"
                                        aria-controls="collapseEight"
                                    >
                                        How long does it take to offset my
                                        stay’s carbon footprint?
                                    </button>
                                </h2>
                                <div
                                    id="collapseEight"
                                    className="accordion-collapse collapse faq-answer"
                                    data-bs-parent="#accordionExample"
                                >
                                    <div className="accordion-body">
                                        <p>
                                            The process begins as soon as your
                                            funds are received. We ensure timely
                                            updates about the impact created. It
                                            would usually take about 2-5 months
                                            to complete the process.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="accordion-item">
                                <h2 className="accordion-header">
                                    <button
                                        className="accordion-button collapsed faq-question"
                                        type="button"
                                        data-bs-toggle="collapse"
                                        data-bs-target="#collapseNine"
                                        aria-expanded="true"
                                        aria-controls="collapseNine"
                                    >
                                        Can I offset footprints for past stays?
                                    </button>
                                </h2>
                                <div
                                    id="collapseNine"
                                    className="accordion-collapse collapse faq-answer"
                                    data-bs-parent="#accordionExample"
                                >
                                    <div className="accordion-body">
                                        <p>
                                            Yes, you can! Just calculate the
                                            footprint of your past stays and
                                            offset its footprint through the
                                            calculator above.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="accordion-item">
                                <h2 className="accordion-header">
                                    <button
                                        className="accordion-button collapsed faq-question"
                                        type="button"
                                        data-bs-toggle="collapse"
                                        data-bs-target="#collapseTen"
                                        aria-expanded="true"
                                        aria-controls="collapseTen"
                                    >
                                        What other initiatives does ReThink
                                        offer?
                                    </button>
                                </h2>
                                <div
                                    id="collapseTen"
                                    className="accordion-collapse collapse faq-answer"
                                    data-bs-parent="#accordionExample"
                                >
                                    <div className="accordion-body">
                                        <p>
                                            ReThink is more than just travel
                                            offsets. We also run:
                                            <ul>
                                                <li>
                                                    <a href="https://rethink.getre.org">
                                                        ReThink Sustainability:
                                                    </a>{" "}
                                                    Your go-to program for
                                                    incorporating micro-actions
                                                    into daily life to reduce
                                                    your environmental impact.
                                                </li>
                                                <li>
                                                    <b>ReThink Events:</b> A
                                                    unique platform to make
                                                    events more sustainable and
                                                    create lasting impact along
                                                    with memories.
                                                </li>
                                            </ul>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </section>
            <div>
                {/* <div
                    style={{
                        boxShadow:
                            "0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.06)",
                        backgroundColor: "#F2F0F2",
                        margin: "20px",
                        padding: "20px",
                        borderRadius: "20px",
                    }}
                >
                    Lorem ipsum dolor sit amet cons. Sed nec elit non nibh.
                    Lorem ipsum dolor sit amet cons. Sed nec elit non nibh.
                </div>
                <div
                    style={{
                        boxShadow:
                            "0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.06)",
                        backgroundColor: "#F2F0F2",
                        margin: "20px",
                        padding: "20px",
                        borderRadius: "20px",
                    }}
                >
                    Lorem ipsum dolor sit amet cons. Sed nec elit non nibh.
                    Lorem ipsum dolor sit amet cons. Sed nec elit non nibh.
                </div>
                <div
                    style={{
                        boxShadow:
                            "0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.06)",
                        backgroundColor: "#F2F0F2",
                        margin: "20px",
                        padding: "20px",
                        borderRadius: "20px",
                    }}
                >
                    Lorem ipsum dolor sit amet cons. Sed nec elit non nibh.
                    Lorem ipsum dolor sit amet cons. Sed nec elit non nibh.
                </div>
                <div
                    style={{
                        boxShadow:
                            "0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.06)",
                        backgroundColor: "#F2F0F2",
                        margin: "20px",
                        padding: "20px",
                        borderRadius: "20px",
                    }}
                >
                    Lorem ipsum dolor sit amet cons. Sed nec elit non nibh.
                    Lorem ipsum dolor sit amet cons. Sed nec elit non nibh.
                </div>
                <div
                    style={{
                        boxShadow:
                            "0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.06)",
                        backgroundColor: "#F2F0F2",
                        margin: "20px",
                        padding: "20px",
                        borderRadius: "20px",
                    }}
                >
                    Lorem ipsum dolor sit amet cons. Sed nec elit non nibh.
                    Lorem ipsum dolor sit amet cons. Sed nec elit non nibh.
                </div> */}
            </div>
            <section className="white-section">
                <h4
                    className="section-heading"
                    style={{
                        marginLeft: "20px",
                        marginRight: "20px",
                    }}
                >
                    Be the Change. Offset Your Stay Today.
                </h4>
                <div className="section-p">
                    Your journey doesn’t have to leave a carbon footprint.
                    Offset it now and join a growing community of responsible
                    travelers. Let's create a world where every journey helps
                    the planet!
                </div>
                <div style={{ textAlign: "center" }}>
                    <button className="offset-btn" onClick={handleScroll}>
                        Offset Now
                    </button>
                </div>
            </section>
            {/* <h1
                style={{
                    textAlign: "center",
                    marginTop: "20px",
                    fontFamily: "Alata",
                }}
            >
                Lorem Ipsum
            </h1>
            <div
                style={{
                    margin: "20px",
                }}
            >
                <img
                    src="assets/images/sample-bg.png"
                    style={{
                        height: "200px",
                        width: "100%",
                    }}
                />
                <div
                    style={{
                        backgroundColor: "#F2F0F2",
                        padding: "20px",
                        textAlign: "center",
                        fontFamily: "Lato",
                        fontSize: "18px",
                        borderBottomRightRadius: "20px",
                        borderBottomLeftRadius: "20px",
                    }}
                >
                    Lorem ipsum dolor sit amet consectetur.
                </div>
            </div>
            <div
                style={{
                    margin: "20px",
                }}
            >
                <img
                    src="assets/images/sample-bg.png"
                    style={{
                        height: "200px",
                        width: "100%",
                    }}
                />
                <div
                    style={{
                        backgroundColor: "#F2F0F2",
                        padding: "20px",
                        textAlign: "center",
                        fontFamily: "Lato",
                        fontSize: "18px",
                        borderBottomRightRadius: "20px",
                        borderBottomLeftRadius: "20px",
                    }}
                >
                    Lorem ipsum dolor sit amet consectetur.
                </div>
            </div>
            <div
                style={{
                    margin: "20px",
                }}
            >
                <img
                    src="assets/images/sample-bg.png"
                    style={{
                        height: "200px",
                        width: "100%",
                    }}
                />
                <div
                    style={{
                        backgroundColor: "#F2F0F2",
                        padding: "20px",
                        textAlign: "center",
                        fontFamily: "Lato",
                        fontSize: "18px",
                        borderBottomRightRadius: "20px",
                        borderBottomLeftRadius: "20px",
                    }}
                >
                    Lorem ipsum dolor sit amet consectetur.
                </div>
            </div>
            <div
                style={{
                    margin: "20px",
                }}
            >
                <img
                    src="assets/images/sample-bg.png"
                    style={{
                        height: "200px",
                        width: "100%",
                    }}
                />
                <div
                    style={{
                        backgroundColor: "#F2F0F2",
                        padding: "20px",
                        textAlign: "center",
                        fontFamily: "Lato",
                        fontSize: "18px",
                        borderBottomRightRadius: "20px",
                        borderBottomLeftRadius: "20px",
                    }}
                >
                    Lorem ipsum dolor sit amet consectetur.
                </div>
            </div>
            <button
                style={{
                    color: "black",
                    marginTop: "20px",
                    fontFamily: "Alata",
                    fontSize: "24px",
                    border: "none",
                    background:
                        "radial-gradient(circle, #BFFDE8 0%, #8DD3BB 100%)",
                    borderRadius: "15px",
                    width: "90%",
                    marginLeft: "20px",
                    marginRight: "20px",
                    padding: "10px 20px",
                    marginBottom: "20px",
                }}
            >
                Calculate Footprint
            </button> */}
        </div>
    );
};

export default Home;
